import { ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-einvoice-text-input',
  templateUrl: './einvoice-text-input.component.html',
  styles: [
    `
      input {
        width: 115px !important;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EInvoiceTextInputComponent),
      multi: true
    }
  ]
})
export class EInvoiceTextInputComponent implements ControlValueAccessor {
  @Input() value: any;
  @Input() disabled = false;
  @Input() maxChar: number = 99;
  @Input() isRequired: boolean = false;
  @Input() isEnabled: boolean = true;
  @Input() placeHolder: string = '';
  @Input() borderless = false;
  @Input() ignoreZero = false;
  @Input() allowEdit = true;
  @Input() allowDot: boolean = false;
  @Input() allowDash: boolean = false;
  @Input() allowComma: boolean = false;
  @Input() allowParenthesis: boolean = false;

  get readonly() {
    if (this.disabled || !this.allowEdit) {
      return true;
    }
    return false;
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(value: number): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onModelChange(val: any) {
    if (val)
      this.value = formatEInvoiceText(
        this.maxChar,
        this.allowDot,
        this.allowDash,
        this.allowComma,
        this.allowParenthesis,
        val
      );
    else this.value = '';
    this.onChange(val);
    this.cdr.markForCheck();
  }

  onKeyUp(event: any) {
    const input = formatEInvoiceText(
      this.maxChar,
      this.allowDot,
      this.allowDash,
      this.allowComma,
      this.allowParenthesis,
      event.target.value
    );

    event.target.value = input;
    return false;
  }

  onKeydown(event: any) {
    if (event.ctrlKey || event.metaKey) return;

    const key = `${event.key}`;
    if (key.length !== 1) return;

    const isAllowedChar =
      /^[a-zA-Z0-9]$/.test(key) ||
      (key === '.' && this.allowDot) ||
      (key === '-' && this.allowDash) ||
      (key === ',' && this.allowComma) ||
      ((key === '(' || key === ')') && this.allowParenthesis);

    if (!isAllowedChar) {
      event.preventDefault();
    }
  }

  onPaste(event: any) {
    const clipData = event.clipboardData?.getData('text');

    if (clipData) {
      // Dynamically build allowed characters based on input flags
      let allowedChars = 'A-Za-z0-9';
      if (this.allowComma) allowedChars += ',';
      if (this.allowDot) allowedChars += '\\.';
      if (this.allowDash) allowedChars += '\\-';
      if (this.allowParenthesis) allowedChars += '\\(\\)';

      // Create regex to exclude spaces and any character not in the allowed list
      const alphaRegex = new RegExp(`[^${allowedChars}]+`, 'g');
      let txtFinal = clipData.replace(alphaRegex, '');

      // If resulting text has filtered out content, set the cleaned text and notify change
      if (txtFinal !== clipData) {
        event.preventDefault(); // Prevent default paste if any filtering occurred
        this.value = txtFinal;
        this.onChange(this.value);
      }
    } else {
      event.preventDefault();
    }
  }

  onClick(e: any) {
    if (!window.getSelection()?.toString()) {
      e.select();
    }
  }
}

export function formatEInvoiceText(maxChar: number, allowDot: boolean, allowDash: boolean, allowComma: boolean, allowParenthesis: boolean, input: string) {
  let result = '';
  let digitCount = 0;

  for (const char of input) {
    const isAllowedChar =
      /^[a-zA-Z0-9]$/.test(char) ||
      (char === '.' && allowDot) ||
      (char === '-' && allowDash) ||
      (char === ',' && allowComma) ||
      ((char === '(' || char === ')') && allowParenthesis);

    if (isAllowedChar) {
      result += char;
      if (++digitCount === maxChar) break;
    }
  }
  return result;
}
